import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSmartBack } from '../hooks/useSmartBack';
import { useTranslation } from 'react-i18next';
import { 
  TrendingUp, TrendingDown, Minus, MapPin, Calendar, RefreshCw, 
  ArrowLeft, Filter, Clock
} from 'lucide-react';
import './MarketPrices.css';

// Sudan states for filtering
const SUDAN_STATES = [
  { key: 'all', name_en: 'All Sudan', name_ar: 'كل السودان' },
  { key: 'khartoum', name_en: 'Khartoum', name_ar: 'الخرطوم' },
  { key: 'gezira', name_en: 'Gezira', name_ar: 'الجزيرة' },
  { key: 'gedaref', name_en: 'Gedaref', name_ar: 'القضارف' },
  { key: 'kassala', name_en: 'Kassala', name_ar: 'كسلا' },
  { key: 'sennar', name_en: 'Sennar', name_ar: 'سنار' },
  { key: 'white_nile', name_en: 'White Nile', name_ar: 'النيل الأبيض' },
  { key: 'blue_nile', name_en: 'Blue Nile', name_ar: 'النيل الأزرق' },
  { key: 'north_kordofan', name_en: 'North Kordofan', name_ar: 'شمال كردفان' },
];

// Default prices
const DEFAULT_PRICES = [
  { cropSlug: 'sesame', cropNameAr: 'سمسم', cropNameEn: 'Sesame', avgPrice: 50000, minPrice: 45000, maxPrice: 55000, unit: 'جوال', unitEn: 'bag', change: 2.5 },
  { cropSlug: 'sorghum', cropNameAr: 'ذرة', cropNameEn: 'Sorghum', avgPrice: 18000, minPrice: 16000, maxPrice: 20000, unit: 'قنطار', unitEn: 'quintar', change: -1.2 },
  { cropSlug: 'groundnuts', cropNameAr: 'فول سوداني', cropNameEn: 'Groundnuts', avgPrice: 35000, minPrice: 32000, maxPrice: 38000, unit: 'جوال', unitEn: 'bag', change: 0 },
  { cropSlug: 'onions', cropNameAr: 'بصل', cropNameEn: 'Onions', avgPrice: 20000, minPrice: 18000, maxPrice: 22000, unit: 'جوال', unitEn: 'bag', change: 5.3 },
  { cropSlug: 'wheat', cropNameAr: 'قمح', cropNameEn: 'Wheat', avgPrice: 22000, minPrice: 20000, maxPrice: 24000, unit: 'قنطار', unitEn: 'quintar', change: -0.5 },
  { cropSlug: 'cotton', cropNameAr: 'قطن', cropNameEn: 'Cotton', avgPrice: 75000, minPrice: 70000, maxPrice: 80000, unit: 'قنطار', unitEn: 'quintar', change: 1.8 },
];

function MarketPrices() {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const goBack = useSmartBack('/');
  const isArabic = i18n.language?.startsWith('ar');
  const isRTL = document.dir === 'rtl' || isArabic;
  
  const [prices, setPrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedState, setSelectedState] = useState('all');
  const [lastUpdated, setLastUpdated] = useState(null);

  // Format price
  const formatPrice = (price) => {
    return new Intl.NumberFormat(isArabic ? 'ar-SD' : 'en-SD').format(price);
  };

  // Fetch prices
  const fetchPrices = useCallback(async () => {
    setLoading(true);
    
    try {
      const params = new URLSearchParams();
      params.append('date', 'today');
      if (selectedState && selectedState !== 'all') {
        params.append('state', selectedState);
      }
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/market-prices?${params.toString()}`
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.prices && data.prices.length > 0) {
          setPrices(data.prices);
          setLastUpdated(data.lastUpdated || new Date().toISOString());
        } else {
          setPrices(DEFAULT_PRICES);
          setLastUpdated(new Date().toISOString());
        }
      } else {
        setPrices(DEFAULT_PRICES);
        setLastUpdated(new Date().toISOString());
      }
    } catch (error) {
      console.error('Failed to fetch market prices:', error);
      setPrices(DEFAULT_PRICES);
      setLastUpdated(new Date().toISOString());
    } finally {
      setLoading(false);
    }
  }, [selectedState]);

  useEffect(() => {
    fetchPrices();
  }, [fetchPrices]);

  // Get change icon
  const getChangeIcon = (change) => {
    if (change > 0) return <TrendingUp size={16} className="change-up" />;
    if (change < 0) return <TrendingDown size={16} className="change-down" />;
    return <Minus size={16} className="change-neutral" />;
  };

  // Get change class
  const getChangeClass = (change) => {
    if (change > 0) return 'positive';
    if (change < 0) return 'negative';
    return 'neutral';
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString(isArabic ? 'ar-SD' : 'en-SD', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="market-prices-page" data-testid="market-prices-page">
      {/* Header */}
      <div className="market-prices-header">
        <button 
          className="back-btn" 
          onClick={goBack}
          aria-label="Go back"
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="page-title">
          {isArabic ? 'أسعار السوق اليوم' : "Today's Market Prices"}
        </h1>
        <button 
          className="refresh-btn"
          onClick={fetchPrices}
          disabled={loading}
          aria-label="Refresh"
        >
          <RefreshCw size={20} className={loading ? 'spinning' : ''} />
        </button>
      </div>
      
      {/* State Filter */}
      <div className="filter-section">
        <div className="filter-row">
          <MapPin size={16} />
          <select
            value={selectedState}
            onChange={(e) => setSelectedState(e.target.value)}
            className="state-select"
            data-testid="market-state-filter"
          >
            {SUDAN_STATES.map((state) => (
              <option key={state.key} value={state.key}>
                {isArabic ? state.name_ar : state.name_en}
              </option>
            ))}
          </select>
        </div>
        
        {lastUpdated && (
          <div className="last-updated">
            <Clock size={14} />
            <span>
              {isArabic ? 'آخر تحديث: ' : 'Last updated: '}
              {formatDate(lastUpdated)}
            </span>
          </div>
        )}
      </div>
      
      {/* Prices Table */}
      <div className="prices-container">
        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>{isArabic ? 'جارٍ تحميل الأسعار...' : 'Loading prices...'}</p>
          </div>
        ) : (
          <div className="prices-table">
            {/* Table Header */}
            <div className="table-header">
              <div className="col-crop">{isArabic ? 'المحصول' : 'Crop'}</div>
              <div className="col-price">{isArabic ? 'السعر' : 'Price'}</div>
              <div className="col-range">{isArabic ? 'النطاق' : 'Range'}</div>
              <div className="col-change">{isArabic ? 'التغير' : 'Change'}</div>
            </div>
            
            {/* Table Body */}
            {prices.map((item) => (
              <div key={item.cropSlug} className="table-row" data-testid={`price-row-${item.cropSlug}`}>
                <div className="col-crop">
                  <span className="crop-name">
                    {isArabic ? item.cropNameAr : item.cropNameEn}
                  </span>
                  <span className="crop-unit">
                    / {isArabic ? item.unit : item.unitEn}
                  </span>
                </div>
                
                <div className="col-price">
                  <span className="price-value">
                    {formatPrice(item.avgPrice)}
                  </span>
                  <span className="price-currency">SDG</span>
                </div>
                
                <div className="col-range">
                  <span className="range-min">{formatPrice(item.minPrice || item.avgPrice * 0.9)}</span>
                  <span className="range-sep">-</span>
                  <span className="range-max">{formatPrice(item.maxPrice || item.avgPrice * 1.1)}</span>
                </div>
                
                <div className={`col-change ${getChangeClass(item.change || 0)}`}>
                  {getChangeIcon(item.change || 0)}
                  <span>{Math.abs(item.change || 0).toFixed(1)}%</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Disclaimer */}
      <div className="disclaimer">
        <p>
          {isArabic 
            ? 'الأسعار تقريبية وقد تختلف حسب جودة المحصول والموقع. آخر تحديث من أسواق السودان الرئيسية.'
            : 'Prices are approximate and may vary based on crop quality and location. Last updated from major Sudan markets.'}
        </p>
      </div>
    </div>
  );
}

export default MarketPrices;
